import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { teachers } from '../../mocks/teachers';
import Navbar from '../home/components/Navbar';

export default function TeacherPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'courses'>('profile');

  const teacher = teachers.find(t => t.id === Number(id));

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!teacher) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f7f5]">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1a2332] mb-4">강사를 찾을 수 없습니다</h1>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#1a2332] text-white rounded-full hover:bg-[#ff6b6b] transition-colors cursor-pointer whitespace-nowrap"
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f7f5]">
      <Navbar isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#1a2332] to-[#2a3342]">
        <div className="max-w-6xl mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-8 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            <span>전체 강사진 보기</span>
          </button>

          <div className="flex items-center gap-12">
            <div className="w-64 h-64 rounded-3xl overflow-hidden shadow-2xl flex-shrink-0 border-4 border-white/20">
              <img
                src={teacher.avatar}
                alt={teacher.name}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-4 py-1.5 bg-[#ff6b6b] text-white text-sm rounded-full font-medium">
                  {teacher.subject}
                </span>
                <span className="px-4 py-1.5 bg-[#4ecdc4] text-white text-sm rounded-full font-medium">
                  {teacher.experience}
                </span>
              </div>

              <h1 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                {teacher.name} <span className="text-2xl font-normal text-gray-300">선생님</span>
              </h1>

              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                {teacher.fullDescription}
              </p>

              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#ff6b6b]">{teacher.stats.students.toLocaleString()}+</div>
                  <div className="text-sm text-gray-400 mt-1">누적 수강생</div>
                </div>
                <div className="w-px h-12 bg-gray-600"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#4ecdc4]">{teacher.stats.perfectScores}</div>
                  <div className="text-sm text-gray-400 mt-1">만점/합격자</div>
                </div>
                <div className="w-px h-12 bg-gray-600"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#ffd93d]">{teacher.stats.satisfaction}%</div>
                  <div className="text-sm text-gray-400 mt-1">학생 만족도</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-[72px] z-40 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-4 px-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'profile'
                  ? 'border-[#ff6b6b] text-[#1a2332] font-semibold'
                  : 'border-transparent text-gray-500 hover:text-[#1a2332]'
              }`}
            >
              프로필
            </button>
            <button
              onClick={() => setActiveTab('courses')}
              className={`py-4 px-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'courses'
                  ? 'border-[#ff6b6b] text-[#1a2332] font-semibold'
                  : 'border-transparent text-gray-500 hover:text-[#1a2332]'
              }`}
            >
              담당 강좌
            </button>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-8">
          {activeTab === 'profile' ? (
            <div className="grid grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="col-span-2 space-y-8">
                {/* Education */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#ff6b6b]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-graduation-cap-line text-xl text-[#ff6b6b]"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-[#1a2332]" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                      학력
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {teacher.education.map((edu, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 bg-[#f7f7f5] rounded-xl">
                        <div className="w-2 h-2 bg-[#ff6b6b] rounded-full flex-shrink-0"></div>
                        <p className="text-gray-700">{edu}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Career */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#4ecdc4]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-briefcase-line text-xl text-[#4ecdc4]"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-[#1a2332]" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                      경력
                    </h2>
                  </div>
                  <div className="space-y-4">
                    {teacher.career.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 bg-[#f7f7f5] rounded-xl">
                        <div className="w-2 h-2 bg-[#4ecdc4] rounded-full flex-shrink-0"></div>
                        <p className="text-gray-700">{item}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Awards */}
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-[#ffd93d]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-award-line text-xl text-[#ffd93d]"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-[#1a2332]" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                      수상 이력
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 gap-4">
                    {teacher.awards.map((award, index) => (
                      <div key={index} className="flex items-center gap-4 p-4 bg-gradient-to-r from-[#ffd93d]/10 to-transparent rounded-xl">
                        <div className="w-10 h-10 bg-[#ffd93d]/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-trophy-line text-[#ffd93d]"></i>
                        </div>
                        <p className="text-gray-700 font-medium">{award}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Philosophy Card */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#ff6b6b]/10 rounded-lg flex items-center justify-center">
                      <i className="ri-heart-line text-xl text-[#ff6b6b]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-[#1a2332]">교육 철학</h3>
                  </div>
                  <div className="relative">
                    <div className="absolute -left-1 top-0 text-4xl text-[#ff6b6b]/20">"</div>
                    <p className="text-gray-700 leading-relaxed pl-6 pr-4 italic">
                      {teacher.philosophy}
                    </p>
                    <div className="absolute -right-1 bottom-0 text-4xl text-[#ff6b6b]/20">"</div>
                  </div>
                </div>

                {/* Contact Card */}
                <div className="bg-[#1a2332] rounded-2xl p-6 text-white">
                  <h3 className="text-lg font-bold mb-2">수강 상담 받기</h3>
                  <p className="text-sm text-gray-300 mb-4">
                    {teacher.name} 선생님의 강좌에 대해 궁금한 점이 있으시면 언제든 문의해주세요
                  </p>
                  <button
                    onClick={() => navigate('/#contact')}
                    className="w-full py-3 bg-[#ff6b6b] text-white rounded-lg font-semibold hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    상담 신청하기
                  </button>
                </div>

                {/* Other Teachers */}
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-[#1a2332] mb-4">다른 강사진</h3>
                  <div className="space-y-3">
                    {teachers.filter(t => t.id !== teacher.id).slice(0, 3).map((t) => (
                      <div
                        key={t.id}
                        onClick={() => navigate(`/teacher/${t.id}`)}
                        className="flex items-center gap-3 p-3 bg-[#f7f7f5] rounded-xl hover:bg-[#ff6b6b]/10 transition-colors cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                          <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#1a2332] text-sm">{t.name}</p>
                          <p className="text-xs text-gray-500 truncate">{t.subject}</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400"></i>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Courses Tab */
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-[#1a2332] mb-4" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  {teacher.name} 선생님 담당 강좌
                </h2>
                <p className="text-gray-600">체계적인 커리큘럼으로 목표 달성을 도와드립니다</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {teacher.courses.map((courseName, index) => {
                  const colorSchemes = [
                    { bg: 'from-[#4ecdc4]/20 to-[#4ecdc4]/5', icon: 'text-[#4ecdc4]', iconBg: 'bg-[#4ecdc4]/20' },
                    { bg: 'from-[#ff6b6b]/20 to-[#ff6b6b]/5', icon: 'text-[#ff6b6b]', iconBg: 'bg-[#ff6b6b]/20' },
                    { bg: 'from-[#ffd93d]/20 to-[#ffd93d]/5', icon: 'text-[#ffd93d]', iconBg: 'bg-[#ffd93d]/20' },
                  ];
                  const colorScheme = colorSchemes[index % 3];
                  const icons = ['ri-medal-line', 'ri-book-open-line', 'ri-pencil-line', 'ri-quill-pen-line'];

                  return (
                    <div
                      key={index}
                      className={`bg-gradient-to-br ${colorScheme.bg} rounded-2xl p-6 hover:shadow-lg transition-all duration-300 cursor-pointer`}
                      onClick={() => navigate(`/course/${index + 1}`)}
                    >
                      <div className={`w-14 h-14 ${colorScheme.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                        <i className={`${icons[index % 4]} text-3xl ${colorScheme.icon}`}></i>
                      </div>
                      <h3 className="text-xl font-bold text-[#1a2332] mb-2">{courseName}</h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {teacher.name} 선생님의 체계적인 커리큘럼으로 실력을 향상시키세요
                      </p>
                      <div className="flex items-center gap-2 text-[#ff6b6b] font-medium text-sm">
                        <span>자세히 보기</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom CTA */}
              <div className="mt-16 bg-gradient-to-r from-[#1a2332] to-[#2a3342] rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Noto Serif KR, serif' }}>
                  {teacher.name} 선생님과 함께 시작하세요
                </h3>
                <p className="text-gray-300 mb-6">
                  체계적인 커리큘럼과 맞춤형 지도로 국어 실력을 한 단계 끌어올리세요
                </p>
                <button
                  onClick={() => navigate('/#contact')}
                  className="px-8 py-4 bg-[#ff6b6b] text-white rounded-full font-semibold hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
                >
                  수강 상담 신청하기
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a2332] py-8">
        <div className="max-w-6xl mx-auto px-4 md:px-8 text-center">
          <p className="text-gray-400 text-xs md:text-sm">
            © 2025 후니국담. All rights reserved. | 
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="ml-2 hover:text-white transition-colors cursor-pointer">
              Website Builder
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
